import React, {
    useState,
    useRef,
    useEffect,
    useLayoutEffect
} from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";

function ColorPicker({ color, setColor, size = 35 }) {
    const [open, setOpen] = useState(false);
    const [hexValue, setHexValue] = useState(
        color || "#ffffff"
    );

    const [pickerPosition, setPickerPosition] = useState(null);

    const pickerRef = useRef(null);
    const buttonRef = useRef(null);

    const presetColors = [
        "#F44A42", // red
        "#F59A4A", // orange
        "#F5E84A", // yellow
        "#55D66A", // green
        "#4DD4DD", // cyan
        "#587BE0", // blue
        "#9466D4", // purple
        "#D85CB8", // pink
        "#444444", // black
        "#FFFFFF", // white
    ];

    /*
     * Keep HEX input synchronized with the actual color.
     *
     * If color is empty/null, the picker displays white,
     * but the actual color remains transparent.
     */
    useEffect(() => {
        setHexValue(color || "#ffffff");
    }, [color]);

    /*
     * Calculate where the popup should appear.
     */
    const getPickerPosition = () => {
        if (!buttonRef.current) {
            return {
                top: 0,
                left: 0,
            };
        }

        const rect =
            buttonRef.current.getBoundingClientRect();

        const pickerWidth = 216;
        const pickerHeight = 350;
        const margin = 8;

        let left = rect.left;
        let top = rect.bottom + margin;

        /*
         * Keep popup inside viewport horizontally.
         */
        if (
            left + pickerWidth >
            window.innerWidth - margin
        ) {
            left =
                window.innerWidth -
                pickerWidth -
                margin;
        }

        if (left < margin) {
            left = margin;
        }

        /*
         * If there isn't enough room below,
         * display popup above the button.
         */
        if (
            top + pickerHeight >
            window.innerHeight - margin
        ) {
            top =
                rect.top -
                pickerHeight -
                margin;
        }

        /*
         * Prevent popup from going above viewport.
         */
        if (top < margin) {
            top = margin;
        }

        return {
            top,
            left,
        };
    };

    /*
     * Calculate popup position BEFORE browser paints it.
     *
     * This prevents the popup from briefly appearing
     * at the top-left corner.
     */
    useLayoutEffect(() => {
        if (!open) {
            setPickerPosition(null);
            return;
        }

        const updatePosition = () => {
            setPickerPosition(
                getPickerPosition()
            );
        };

        updatePosition();

        window.addEventListener(
            "resize",
            updatePosition
        );

        window.addEventListener(
            "scroll",
            updatePosition,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                updatePosition
            );

            window.removeEventListener(
                "scroll",
                updatePosition,
                true
            );
        };
    }, [open]);

    /*
     * Close popup when clicking outside.
     */
    useEffect(() => {
        if (!open) {
            return;
        }

        const handleOutsideClick = (event) => {
            const clickedButton =
                buttonRef.current &&
                buttonRef.current.contains(
                    event.target
                );

            const clickedPicker =
                pickerRef.current &&
                pickerRef.current.contains(
                    event.target
                );

            if (
                !clickedButton &&
                !clickedPicker
            ) {
                setOpen(false);
            }
        };

        document.addEventListener(
            "pointerdown",
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                "pointerdown",
                handleOutsideClick
            );
        };
    }, [open]);

    /*
     * Open / close picker.
     *
     * IMPORTANT:
     * We deliberately DO NOT set a default color here.
     *
     * This means transparent remains transparent
     * until the user actually chooses a color.
     */
    const handleOpen = () => {
        setOpen((prev) => !prev);
    };

    /*
     * HEX input changed.
     */
    const handleHexChange = (e) => {
        let value = e.target.value;

        /*
         * Automatically add #.
         */
        if (!value.startsWith("#")) {
            value = "#" + value;
        }

        setHexValue(value);

        /*
         * Only update the actual color once
         * we have a valid 6-digit HEX value.
         */
        if (
            /^#[0-9A-Fa-f]{6}$/.test(value)
        ) {
            setColor(
                value.toUpperCase()
            );
        }
    };

    /*
     * Restore current color if HEX input
     * is invalid when leaving the field.
     */
    const handleHexBlur = () => {
        if (
            !/^#[0-9A-Fa-f]{6}$/.test(
                hexValue
            )
        ) {
            setHexValue(
                color || "#ffffff"
            );
        }
    };

    /*
     * Color picker changed.
     */
    const handleColorChange = (
        newColor
    ) => {
        const normalizedColor =
            newColor.toUpperCase();

        setColor(normalizedColor);
        setHexValue(
            normalizedColor
        );
    };

    /*
     * Explicitly restore transparent.
     */
    const handleTransparent = () => {
        setColor("");
        setHexValue("#ffffff");
    };

    /*
     * react-colorful needs an actual color.
     *
     * If the actual value is empty, white is used
     * ONLY visually inside the picker.
     *
     * It does NOT change the actual value.
     */
    const pickerColor =
        color || "#ffffff";

    return (
        <>
            {/* =====================================================
                COLOR BUTTON
            ====================================================== */}

            <label
                ref={buttonRef}
                title="Select answer color"
                onClick={handleOpen}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",

                    marginBottom: "0px",

                    width: `${size}px`,
                    height: `${size}px`,

                    cursor: "pointer",

                    border:
                        "1px solid #ccc",

                    borderRadius: "3px",

                    /*
                     * Empty color remains transparent.
                     */
                    backgroundColor:
                        color ||
                        "transparent",

                    position: "relative",

                    boxSizing:
                        "border-box",
                }}
            >
                <i
                    className="fas fa-eye-dropper"
                    style={{
                        color: color
                            ? "#fff"
                            : "#666",

                        textShadow: color
                            ? "0 0 2px #000"
                            : "none",

                        fontSize:
                            size <= 20
                                ? "10px"
                                : undefined,
                    }}
                />
            </label>

            {/* =====================================================
                COLOR PICKER POPUP
            ====================================================== */}

            {open &&
                pickerPosition &&
                createPortal(
                    <div
                        ref={pickerRef}
                        style={{
                            position:
                                "fixed",

                            top: `${pickerPosition.top}px`,

                            left: `${pickerPosition.left}px`,

                            /*
                             * Very high z-index so the picker
                             * appears above virtually everything.
                             */
                            zIndex:
                                2147483647,

                            padding: "12px",

                            background:
                                "#fff",

                            border:
                                "1px solid #ccc",

                            borderRadius:
                                "6px",

                            boxShadow:
                                "0 4px 20px rgba(0, 0, 0, 0.25)",

                            width: "216px",

                            boxSizing:
                                "border-box",
                        }}
                    >

                        {/* =================================================
                            COLOR PICKER
                        ================================================== */}

                        <HexColorPicker
                            color={
                                pickerColor
                            }
                            onChange={
                                handleColorChange
                            }
                            style={{
                                width:
                                    "100%",
                            }}
                        />

                        {/* =================================================
                            HEX INPUT
                        ================================================== */}

                        <div
                            style={{
                                marginTop:
                                    "12px",
                            }}
                        >
                            <label
                                style={{
                                    display:
                                        "block",

                                    fontSize:
                                        "12px",

                                    fontWeight:
                                        "600",

                                    color:
                                        "#555",

                                    marginBottom:
                                        "5px",
                                }}
                            >
                                HEX
                            </label>

                            <input
                                type="text"
                                value={
                                    hexValue
                                }
                                onChange={
                                    handleHexChange
                                }
                                onBlur={
                                    handleHexBlur
                                }
                                onFocus={(e) =>
                                    e.target.select()
                                }
                                maxLength={7}
                                spellCheck={
                                    false
                                }
                                style={{
                                    width:
                                        "100%",

                                    height:
                                        "32px",

                                    boxSizing:
                                        "border-box",

                                    padding:
                                        "4px 8px",

                                    border:
                                        "1px solid #ccc",

                                    borderRadius:
                                        "4px",

                                    fontSize:
                                        "14px",

                                    fontFamily:
                                        "monospace",

                                    outline:
                                        "none",
                                }}
                            />
                        </div>

                        {/* =================================================
                            TRANSPARENT BUTTON
                        ================================================== */}

                        <button
                            type="button"
                            onClick={
                                handleTransparent
                            }
                            style={{
                                width:
                                    "100%",

                                height:
                                    "30px",

                                marginTop:
                                    "10px",

                                border:
                                    "1px solid #ccc",

                                borderRadius:
                                    "4px",

                                /*
                                 * Checkerboard pattern
                                 * represents transparency.
                                 */
                                background:
                                    "linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%), linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%)",

                                backgroundPosition:
                                    "0 0, 6px 6px",

                                backgroundSize:
                                    "12px 12px",

                                cursor:
                                    "pointer",

                                fontSize:
                                    "12px",

                                color:
                                    "#555",
                            }}
                        >
                            Transparent
                        </button>

                        {/* =================================================
                            PRESET COLORS
                        ================================================== */}

                        <div
                            style={{
                                marginTop:
                                    "12px",
                            }}
                        >
                            <div
                                style={{
                                    fontSize:
                                        "12px",

                                    fontWeight:
                                        "600",

                                    color:
                                        "#555",

                                    marginBottom:
                                        "6px",
                                }}
                            >
                                Presets
                            </div>

                            <div
                                style={{
                                    display:
                                        "grid",

                                    gridTemplateColumns:
                                        "repeat(5, 1fr)",

                                    gap: "6px",
                                }}
                            >
                                {presetColors.map(
                                    (
                                        preset
                                    ) => (
                                        <button
                                            key={
                                                preset
                                            }
                                            type="button"
                                            title={
                                                preset
                                            }
                                            onClick={() =>
                                                handleColorChange(
                                                    preset
                                                )
                                            }
                                            style={{
                                                width:
                                                    "30px",

                                                height:
                                                    "30px",

                                                padding:
                                                    0,

                                                borderRadius:
                                                    "4px",

                                                border:
                                                    color?.toUpperCase() ===
                                                    preset
                                                        ? "2px solid #333"
                                                        : "1px solid #ccc",

                                                backgroundColor:
                                                    preset,

                                                cursor:
                                                    "pointer",

                                                boxSizing:
                                                    "border-box",
                                            }}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </div>,

                    document.body
                )}
        </>
    );
}

export default ColorPicker;
